import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { useLanguage } from '../../context/LanguageContext';

const API = '/api/v1/vsm';

const PRESET_COLORS = [
  '#1976D2', '#0D47A1', '#FF6F00', '#388E3C',
  '#7B1FA2', '#D32F2F', '#0097A7', '#5D4037',
  '#455A64', '#E91E63', '#F57C00', '#00ACC1'
];

const VSMServiceTypes = () => {
  const { lang } = useLanguage();
  const isRTL = lang === 'ar';

  const T = {
    title:           { ar: '🧰 أنواع الخدمات', en: '🧰 Service Types' },
    subtitle:        { ar: 'نوع خدمة', en: 'service type(s)' },
    add:             { ar: '+ إضافة خدمة', en: '+ Add Service' },
    edit:            { ar: '✏️ تعديل الخدمة', en: '✏️ Edit Service' },
    create:          { ar: '➕ خدمة جديدة', en: '➕ New Service' },
    search:          { ar: 'ابحث باسم الخدمة...', en: 'Search by name...' },
    basicInfo:       { ar: 'بيانات الخدمة', en: 'Service Details' },
    pricing:         { ar: 'التسعير والمدة', en: 'Pricing & Duration' },
    appearance:      { ar: 'المظهر', en: 'Appearance' },
    nameEn:          { ar: 'اسم الخدمة (إنجليزي)', en: 'Service Name (English)' },
    nameAr:          { ar: 'اسم الخدمة (عربي)', en: 'Service Name (Arabic)' },
    descriptionEn:   { ar: 'الوصف (إنجليزي)', en: 'Description (English)' },
    descriptionAr:   { ar: 'الوصف (عربي)', en: 'Description (Arabic)' },
    duration:        { ar: 'المدة الافتراضية (دقيقة)', en: 'Default Duration (min)' },
    price:           { ar: 'السعر الافتراضي', en: 'Default Price' },
    color:           { ar: 'لون التصنيف', en: 'Category Color' },
    displayOrder:    { ar: 'ترتيب العرض', en: 'Display Order' },
    active:          { ar: 'نشط', en: 'Active' },
    save:            { ar: '💾 حفظ', en: '💾 Save' },
    saving:          { ar: 'جاري الحفظ...', en: 'Saving...' },
    cancel:          { ar: 'إلغاء', en: 'Cancel' },
    confirmDelete:   { ar: 'هل أنت متأكد من حذف هذه الخدمة؟', en: 'Delete this service type?' },
    actions:         { ar: 'الإجراءات', en: 'Actions' },
    loading:         { ar: 'جاري التحميل...', en: 'Loading...' },
    noServices:      { ar: 'لا توجد خدمات. اضغط "إضافة خدمة" للبدء.', en: 'No services. Click "Add Service" to start.' },
    nameRequired:    { ar: 'اسم الخدمة مطلوب (عربي وإنجليزي)', en: 'Service name is required (both AR and EN)' },
    saveError:       { ar: 'فشل حفظ البيانات', en: 'Failed to save' },
    deleteError:     { ar: 'فشل حذف الخدمة', en: 'Failed to delete' },
    minutes:         { ar: 'دقيقة', en: 'min' },
    sar:             { ar: 'ر.س', en: 'SAR' },
  };

  const tr = (key) => T[key]?.[lang] || T[key]?.en || key;

  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);

  const emptyForm = {
    name: '', name_ar: '',
    description: '', description_ar: '',
    default_duration_min: 30,
    default_price: '',
    color: '#1976D2',
    display_order: 0,
    is_active: true
  };
  const [form, setForm] = useState({ ...emptyForm });

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  // Fetch
  const fetchServices = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    const params = new URLSearchParams();
    if (search) params.append('search', search);

    try {
      const res = await fetch(`${API}/service-types?${params}`, { headers });
      const data = await res.json();
      if (data.success) setServices(data.data);
    } catch (err) {
      console.error('[VSM] Fetch services error:', err);
    }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  const resetForm = () => { setForm({ ...emptyForm }); setEditId(null); };

  const openCreate = () => { resetForm(); setShowForm(true); };

  const openEdit = (svc, e) => {
    e?.stopPropagation();
    setForm({
      name: svc.name || '',
      name_ar: svc.name_ar || '',
      description: svc.description || '',
      description_ar: svc.description_ar || '',
      default_duration_min: svc.default_duration_min || 30,
      default_price: svc.default_price !== null ? String(svc.default_price) : '',
      color: svc.color || '#1976D2',
      display_order: svc.display_order || 0,
      is_active: !!svc.is_active
    });
    setEditId(svc.id);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name?.trim() || !form.name_ar?.trim()) {
      alert(tr('nameRequired'));
      return;
    }
    setSaving(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    try {
      const url = editId ? `${API}/service-types/${editId}` : `${API}/service-types`;
      const method = editId ? 'PUT' : 'POST';
      const res = await fetch(url, { method, headers, body: JSON.stringify(form) });
      const data = await res.json();
      if (data.success) {
        setShowForm(false);
        resetForm();
        fetchServices();
      } else {
        alert(data.error || tr('saveError'));
      }
    } catch (err) {
      console.error('[VSM] Save service error:', err);
      alert(tr('saveError'));
    }
    setSaving(false);
  };

  const handleDelete = async (svc, e) => {
    e?.stopPropagation();
    if (!window.confirm(`${tr('confirmDelete')}\n\n${isRTL ? svc.name_ar : svc.name}`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/service-types/${svc.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) fetchServices();
      else alert(data.error || tr('deleteError'));
    } catch (err) {
      console.error('[VSM] Delete service error:', err);
      alert(tr('deleteError'));
    }
  };

  // Styles
  const inputStyle = {
    width: '100%', padding: '12px', borderRadius: '8px',
    border: '2px solid rgba(255,255,255,0.2)', backgroundColor: '#1a2038',
    color: '#f9fafb', fontSize: '13px', outline: 'none', boxSizing: 'border-box'
  };
  const labelStyle = {
    fontSize: '11px', color: '#94a3b8', fontWeight: '600',
    display: 'block', marginBottom: '4px'
  };
  const sectionStyle = {
    background: 'rgba(15,23,42,0.3)', borderRadius: '10px',
    padding: '16px', marginBottom: '14px',
    border: '1px solid rgba(255,255,255,0.06)'
  };
  const sectionTitle = (icon, text) => (
    <h3 style={{ fontSize: '13px', fontWeight: '700', color: '#818cf8',
      margin: '0 0 12px', display: 'flex', alignItems: 'center', gap: '6px' }}>{icon} {text}</h3>
  );
  const modalOverlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
  };
  const modalBox = {
    background: '#1e293b', borderRadius: '16px', padding: '24px',
    maxHeight: '90vh', overflow: 'auto',
    border: '1px solid rgba(255,255,255,0.1)',
    direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
  };

  return (
    <Layout>
      <div style={{
        padding: '24px', maxWidth: '1200px', margin: '0 auto',
        direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left'
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#f1f5f9', margin: 0 }}>
              {tr('title')}
            </h1>
            <p style={{ fontSize: '13px', color: '#94a3b8', margin: '4px 0 0' }}>
              {services.length} {tr('subtitle')}
            </p>
          </div>
          <button
            onClick={openCreate}
            style={{
              background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
              border: 'none', padding: '9px 18px', borderRadius: '8px',
              fontWeight: '600', fontSize: '13px', cursor: 'pointer'
            }}
          >
            {tr('add')}
          </button>
        </div>

        {/* Search */}
        <input
          type="text"
          placeholder={tr('search')}
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, maxWidth: '350px', marginBottom: '16px' }}
        />

        {/* Cards Grid */}
        {loading ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8',
            background: 'rgba(30,41,59,0.5)', borderRadius: '12px' }}>
            {tr('loading')}
          </div>
        ) : services.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8',
            background: 'rgba(30,41,59,0.5)', borderRadius: '12px' }}>
            {tr('noServices')}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '14px'
          }}>
            {services.map(svc => (
              <div
                key={svc.id}
                style={{
                  background: 'rgba(30,41,59,0.6)',
                  borderRadius: '12px',
                  padding: '18px',
                  border: `1px solid rgba(255,255,255,0.08)`,
                  borderLeft: `4px solid ${svc.color || '#1976D2'}`,
                  position: 'relative',
                  transition: 'transform 0.15s, box-shadow 0.15s',
                  cursor: 'pointer'
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.3)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                onClick={(e) => openEdit(svc, e)}
              >
                {/* Inactive badge */}
                {!svc.is_active && (
                  <span style={{
                    position: 'absolute', top: '8px',
                    [isRTL ? 'left' : 'right']: '8px',
                    background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                    fontSize: '10px', padding: '2px 6px', borderRadius: '4px'
                  }}>
                    Inactive
                  </span>
                )}

                {/* Color badge + name */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{
                    background: svc.color || '#1976D2',
                    width: '28px', height: '28px', borderRadius: '50%',
                    display: 'inline-block', flexShrink: 0
                  }} />
                  <h3 style={{
                    margin: 0, fontSize: '15px', fontWeight: '700',
                    color: '#f1f5f9'
                  }}>
                    {isRTL ? svc.name_ar : svc.name}
                  </h3>
                </div>

                {/* Other-language name */}
                <div style={{
                  fontSize: '12px', color: '#64748b',
                  marginBottom: '12px', marginLeft: '36px'
                }} dir={isRTL ? 'ltr' : 'rtl'}>
                  {isRTL ? svc.name : svc.name_ar}
                </div>

                {/* Description */}
                {(isRTL ? svc.description_ar : svc.description) && (
                  <p style={{
                    margin: '0 0 12px', fontSize: '12px',
                    color: '#94a3b8', lineHeight: 1.5
                  }}>
                    {isRTL ? svc.description_ar : svc.description}
                  </p>
                )}

                {/* Stats: duration + price */}
                <div style={{
                  display: 'flex', gap: '12px',
                  paddingTop: '12px',
                  borderTop: '1px solid rgba(255,255,255,0.06)'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '2px' }}>
                      ⏱ {tr('duration')}
                    </div>
                    <div style={{ fontSize: '14px', color: '#e2e8f0', fontWeight: '600' }}>
                      {svc.default_duration_min} {tr('minutes')}
                    </div>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: '10px', color: '#64748b', marginBottom: '2px' }}>
                      💰 {tr('price')}
                    </div>
                    <div style={{ fontSize: '14px', color: '#10b981', fontWeight: '600' }}>
                      {svc.default_price !== null
                        ? `${parseFloat(svc.default_price).toFixed(2)} ${tr('sar')}`
                        : '—'}
                    </div>
                  </div>
                </div>

                {/* Actions */}
                <div style={{
                  display: 'flex', gap: '6px', justifyContent: 'flex-end',
                  marginTop: '12px',
                  paddingTop: '10px',
                  borderTop: '1px solid rgba(255,255,255,0.04)'
                }}>
                  <button
                    onClick={(e) => openEdit(svc, e)}
                    style={{
                      background: 'rgba(99,102,241,0.2)', color: '#a5b4fc',
                      border: '1px solid rgba(99,102,241,0.3)',
                      padding: '5px 10px', borderRadius: '6px',
                      fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                    }}
                  >✏️</button>
                  <button
                    onClick={(e) => handleDelete(svc, e)}
                    style={{
                      background: 'rgba(239,68,68,0.15)', color: '#fca5a5',
                      border: '1px solid rgba(239,68,68,0.25)',
                      padding: '5px 10px', borderRadius: '6px',
                      fontSize: '12px', cursor: 'pointer', fontWeight: '600'
                    }}
                  >🗑️</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Form Modal */}
        {showForm && (
          <div style={modalOverlay} onClick={() => setShowForm(false)}>
            <div style={{ ...modalBox, width: '600px', maxWidth: '95vw' }} onClick={e => e.stopPropagation()}>
              <h2 style={{ fontSize: '18px', fontWeight: '700', color: '#f1f5f9', margin: '0 0 16px' }}>
                {editId ? tr('edit') : tr('create')}
              </h2>
              <form onSubmit={handleSubmit}>

                {/* Basic */}
                <div style={sectionStyle}>
                  {sectionTitle('🏷️', tr('basicInfo'))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>{tr('nameEn')} *</label>
                      <input
                        value={form.name}
                        onChange={e => handleChange('name', e.target.value)}
                        style={inputStyle}
                        placeholder="Exterior Wash"
                        required
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('nameAr')} *</label>
                      <input
                        value={form.name_ar}
                        onChange={e => handleChange('name_ar', e.target.value)}
                        style={{ ...inputStyle, textAlign: 'right' }}
                        dir="rtl"
                        placeholder="غسيل خارجي"
                        required
                      />
                    </div>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginTop: '10px' }}>
                    <div>
                      <label style={labelStyle}>{tr('descriptionEn')}</label>
                      <textarea
                        value={form.description}
                        onChange={e => handleChange('description', e.target.value)}
                        style={{ ...inputStyle, minHeight: '50px', resize: 'vertical' }}
                        rows={2}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('descriptionAr')}</label>
                      <textarea
                        value={form.description_ar}
                        onChange={e => handleChange('description_ar', e.target.value)}
                        style={{ ...inputStyle, minHeight: '50px', resize: 'vertical', textAlign: 'right' }}
                        dir="rtl"
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                {/* Pricing */}
                <div style={sectionStyle}>
                  {sectionTitle('💰', tr('pricing'))}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px' }}>
                    <div>
                      <label style={labelStyle}>{tr('duration')}</label>
                      <input
                        type="number"
                        min="1"
                        value={form.default_duration_min}
                        onChange={e => handleChange('default_duration_min', e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('price')} ({tr('sar')})</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        value={form.default_price}
                        onChange={e => handleChange('default_price', e.target.value)}
                        style={inputStyle}
                        placeholder="0.00"
                      />
                    </div>
                    <div>
                      <label style={labelStyle}>{tr('displayOrder')}</label>
                      <input
                        type="number"
                        value={form.display_order}
                        onChange={e => handleChange('display_order', e.target.value)}
                        style={inputStyle}
                      />
                    </div>
                  </div>
                </div>

                {/* Appearance */}
                <div style={sectionStyle}>
                  {sectionTitle('🎨', tr('appearance'))}
                  <label style={labelStyle}>{tr('color')}</label>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '6px' }}>
                    {PRESET_COLORS.map(c => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => handleChange('color', c)}
                        style={{
                          width: '34px', height: '34px',
                          borderRadius: '50%',
                          background: c,
                          border: form.color === c ? '3px solid #fff' : '2px solid rgba(255,255,255,0.2)',
                          cursor: 'pointer',
                          transition: 'transform 0.1s',
                          transform: form.color === c ? 'scale(1.1)' : 'scale(1)'
                        }}
                      />
                    ))}
                    <input
                      type="color"
                      value={form.color}
                      onChange={e => handleChange('color', e.target.value)}
                      style={{
                        width: '34px', height: '34px',
                        borderRadius: '50%', border: '2px solid rgba(255,255,255,0.2)',
                        cursor: 'pointer', background: 'transparent', padding: 0
                      }}
                    />
                  </div>
                  <div style={{ marginTop: '12px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', color: '#cbd5e1' }}>
                      <input
                        type="checkbox"
                        checked={form.is_active}
                        onChange={e => handleChange('is_active', e.target.checked)}
                        style={{ width: '16px', height: '16px' }}
                      />
                      {tr('active')}
                    </label>
                  </div>
                </div>

                {/* Buttons */}
                <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px', flexDirection: isRTL ? 'row-reverse' : 'row' }}>
                  <button
                    type="button"
                    onClick={() => { setShowForm(false); resetForm(); }}
                    style={{
                      background: 'rgba(100,116,139,0.2)', color: '#cbd5e1',
                      border: '1px solid rgba(100,116,139,0.3)',
                      padding: '10px 20px', borderRadius: '8px',
                      fontSize: '13px', cursor: 'pointer', fontWeight: '600'
                    }}
                  >{tr('cancel')}</button>
                  <button
                    type="submit"
                    disabled={saving}
                    style={{
                      background: saving ? 'rgba(99,102,241,0.3)' : 'linear-gradient(135deg,#6366f1,#8b5cf6)',
                      color: '#fff', border: 'none',
                      padding: '10px 24px', borderRadius: '8px',
                      fontSize: '13px', cursor: saving ? 'not-allowed' : 'pointer',
                      fontWeight: '700'
                    }}
                  >{saving ? tr('saving') : tr('save')}</button>
                </div>

              </form>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default VSMServiceTypes;
